import {DrupalMedia} from "next-drupal";
import {useRouter} from "next/router";
import {useContext, useEffect, useState} from "react";
import {Dictionary} from "../../../types/hygiena-types";
import {DictionaryContext} from "../../../context/dictionary-context";
import {FrostedGlassContainer} from "../../atoms/gradient-container";
import {Hero} from "../hero";
import {FormattedText} from "../../formatted-text";
import {ImageIconUrl} from "../../atoms/icon";

/**
 * Displays a search field that triggers a debounced callback on keyup.
 *
 * @param title
 *   The page title.
 * @param image
 *   The background image.
 * @param headline
 *   The headline text.
 * @param searchPlaceholder
 *   The placeholder for the search input.
 * @param searchCallback
 *   The callback on input entry.
 * @constructor
 */
export function SearchHero({title, image, headline, searchPlaceholder, searchCallback, small}:
                             {
                               title: string,
                               image?: DrupalMedia,
                               headline?: string,
                               searchPlaceholder?: string,
                               searchCallback?: Function,
                               small?: boolean,
                             }):JSX.Element {
  const router = useRouter();
  const [ready, setReady] = useState(false);
  const [searchText, setSearchText] = useState(router?.query?.s);
  const t = useContext<Dictionary>(DictionaryContext);

  // @todo also used in document search, maybe we can make this generic.
  // Debounce the search text if we're using it.
  useEffect(() => {
    // Set search text on load if there is a query "s" param.
    if (router.isReady && router?.query?.s && !ready) {
      setSearchText(router?.query?.s);
      setReady(true);
    }
    if (searchCallback) {
      if (ready) {
        const handler = setTimeout(() => {
          searchCallback(searchText);
        }, 500);

        return () => {
          clearTimeout(handler);
        }
      }
      setReady(true);
    }
  }, [searchText, router.isReady]);

  return (
    <Hero image={image} small={small}>
      <FrostedGlassContainer className="w-full rounded-none md:rounded-sm drop-shadow-sm px-4 md:px-12 pt-10 pb-12 text-black flex flex-col justify-center items-center">
        <h1 className="text-center">{title}</h1>
        <div className="text-center text-xl mb-10 leading-8">
          <FormattedText processed={headline ?? ""}/>
        </div>
        {searchCallback && (
          <form className="w-full lg:w-2/5" onSubmit={event => event.preventDefault()}>
            <input type="text"
                   style={{backgroundImage: `url(${ImageIconUrl("search-sm", "dark-gray")})`, backgroundRepeat: "no-repeat", backgroundPosition: ".5rem center", backgroundSize: "18px"}}
                   className="p-3 pl-8 w-full placeholder:text-gray-500 outline-primary rounded-sm border border-gray-300"
                   placeholder={searchPlaceholder ?? `${t?.documents?.search}`}
                   defaultValue={searchText}
                   onChange={event => setSearchText(event.target.value)}
            />
          </form>
        )}
      </FrostedGlassContainer>
    </Hero>
  )
}
